import { createContext, useContext, useEffect, useRef, type ReactNode } from 'react';
import { notifications } from '@mantine/notifications';
import {
  useConnectivity,
  useOfflineQueue,
  setupOfflineInterceptor,
  OfflineQueuedError,
  OfflineIndicator,
} from '../../../shared/src/offline';
import type { OfflineState } from '../../../shared/src/offline';
import api from '@/config/api';

interface OfflineContextValue extends OfflineState {
  processQueue: () => Promise<void>;
}

const OfflineContext = createContext<OfflineContextValue>({
  isOnline: true,
  pendingCount: 0,
  isSyncing: false,
  lastSyncAt: null,
  lastError: null,
  processQueue: async () => {},
});

export function useOffline() {
  return useContext(OfflineContext);
}

function getFreshHeaders(): Record<string, string> {
  const headers: Record<string, string> = {};
  const csrfMatch = document.cookie.match(/(?:^|;\s*)freshops_csrf=([^;]*)/);
  if (csrfMatch) {
    headers['X-CSRF-Token'] = decodeURIComponent(csrfMatch[1]);
  }
  return headers;
}

export default function OfflineProvider({ children }: { children: ReactNode }) {
  const { isOnline } = useConnectivity();
  const { pendingCount, isSyncing, lastSyncAt, lastError, addMutation, processQueue } =
    useOfflineQueue(isOnline, getFreshHeaders);

  // Stable ref so the interceptor always calls the latest addMutation without re-registering
  const addMutationRef = useRef(addMutation);
  addMutationRef.current = addMutation;

  // Register offline interceptor once on mount, eject on unmount
  useEffect(() => {
    const cleanup = setupOfflineInterceptor(
      api,
      (mutation) => addMutationRef.current(mutation),
    );
    return cleanup;
  }, []);

  // Show toast when a mutation is queued offline — also registered once
  useEffect(() => {
    const interceptorId = api.interceptors.response.use(undefined, (error) => {
      if (error instanceof OfflineQueuedError) {
        notifications.show({
          title: 'Saved Offline',
          message: 'Your change has been queued and will sync when you reconnect.',
          color: 'blue',
          autoClose: 4000,
        });
      }
      return Promise.reject(error);
    });

    return () => {
      api.interceptors.response.eject(interceptorId);
    };
  }, []);

  const value: OfflineContextValue = {
    isOnline,
    pendingCount,
    isSyncing,
    lastSyncAt,
    lastError,
    processQueue,
  };

  return (
    <OfflineContext.Provider value={value}>
      <OfflineIndicator
        isOnline={isOnline}
        pendingCount={pendingCount}
        isSyncing={isSyncing}
        lastSyncAt={lastSyncAt}
        lastError={lastError}
        onSync={processQueue}
      />
      {children}
    </OfflineContext.Provider>
  );
}
